import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Link, Outlet, useMatches } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { lazy, Suspense } from "react";
import { ErrorState } from "@/components/error-state";
import { Spinner } from "@/components/spinner";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button, buttonVariants } from "@/components/ui/button";
import { Wordmark } from "@/components/wordmark";
import { useSession } from "@/lib/auth-client";
import type { tools } from "@/lib/tools";
import { useUiStore } from "@/stores/ui";

const Devtools =
  import.meta.env.PROD || import.meta.env.VITE_DEVTOOLS !== "true"
    ? () => null
    : lazy(async () => {
        const [{ TanStackRouterDevtools }, { ReactQueryDevtools }] = await Promise.all([
          import("@tanstack/react-router-devtools"),
          import("@tanstack/react-query-devtools"),
        ]);
        return {
          default: () => (
            <>
              <TanStackRouterDevtools position="bottom-right" />
              <ReactQueryDevtools buttonPosition="bottom-left" />
            </>
          ),
        };
      });

export interface RouterContext {
  queryClient: QueryClient;
  tools: typeof tools;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: ErrorState,
  notFoundComponent: () => (
    <div className="p-4 text-center md:p-8">
      <h2 className="font-medium font-serif text-2xl">Seite nicht gefunden</h2>
      <Link to="/" className="text-sm underline decoration-accent underline-offset-4">
        Zur Startseite
      </Link>
    </div>
  ),
});

function RootLayout() {
  const { data: session, isPending } = useSession();
  const { openMobileSidebar } = useUiStore();
  // AppSidebar only mounts inside the `/_authed` layout route — the
  // hamburger only makes sense (and only has a drawer to open) there too.
  const matches = useMatches();
  const isAuthedShell = matches.some((match) => match.routeId === "/_authed");
  // The header's sign-in button would only duplicate the form on /login.
  const isLogin = matches.some((match) => match.routeId === "/login");

  return (
    <div className="flex min-h-dvh flex-col">
      <header
        className={`flex items-center gap-4 border-b px-6 py-3 ${isAuthedShell ? "md:hidden" : ""}`}
      >
        {isAuthedShell ? (
          <Button
            variant="ghost"
            size="icon"
            className="-ml-2 md:hidden"
            onClick={openMobileSidebar}
            aria-label="Seitenleiste öffnen"
          >
            <Menu className="size-4" />
          </Button>
        ) : null}
        <Wordmark />
        {isPending || session ? null : (
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            {isLogin ? null : (
              <Link to="/login" className={buttonVariants({ size: "sm" })}>
                Anmelden
              </Link>
            )}
          </div>
        )}
      </header>

      <div role={isAuthedShell ? undefined : "main"} className="flex flex-1 flex-col">
        <Suspense fallback={<Spinner />}>
          <Outlet />
        </Suspense>
      </div>

      <Suspense>
        <Devtools />
      </Suspense>
    </div>
  );
}
